import { Checkbox } from '@/components/ui/checkbox';
import { Text } from '@/components/ui/text';
import { H2 } from '@/components/ui/typography';
import { PersonaImportContext } from '@/contexts/PersonaImportContext';
import { useLocalSearchParams } from 'expo-router';
import Head from 'expo-router/head';
import * as WebBrowser from 'expo-web-browser';
import { Fragment, useCallback, useContext } from 'react';
import {
  Image,
  Linking,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  View
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

const IMAGE_SOURCE = require('../../assets/images/persona1.png');

export default function PersonaIndex() {
  const { first } = useLocalSearchParams();

  return (
    <Fragment>
      <Head>
        <title>New Persona - Lexikon AI</title>
      </Head>

      <SafeAreaView edges={['right', 'bottom', 'left']} className="flex-1">
        <View className="flex h-full">
          <ScrollView contentContainerClassName="flex">
            <View className="flex px-12 pt-4 web:lg:w-[50%] web:self-center">
              <View className="items-center pb-1">
                <Image
                  source={IMAGE_SOURCE}
                  style={styles.image}
                  resizeMode="contain"
                />
                <H2 className="border-0 pb-1 pt-4 text-center">
                  {first ? 'Create Your First Persona' : 'Adding a Persona'}
                </H2>

                <Text className="pt-4">
                  To create your Persona, you'll need to make a screen recording
                  of a text conversation you've had with someone. This could be
                  from any messaging app like WhatsApp, Discord, Slack, or
                  iMessage.
                </Text>

                <Text className="pt-4">
                  Simply record your screen while scrolling through the
                  conversation (up to 1 minute on the free plan, 5 minutes with
                  premium). We'll process this recording and develop a Digital
                  Double that mirrors their communication style, personality,
                  and remembers the context of your conversations.
                </Text>

                <Text className="pt-4">
                  Once uploaded, your screen recording will be securely
                  processed and automatically deleted within 24 hours.
                </Text>
              </View>
            </View>
          </ScrollView>
          <View className="px-12 web:mx-auto mt-4">
            <AcceptTerms />
          </View>
        </View>
      </SafeAreaView>
    </Fragment>
  );
}

function AcceptTerms() {
  const { personaImport, updatePersonaImport } =
    useContext(PersonaImportContext);
  const checked = personaImport.acceptedTerms;
  const setChecked = (checked: boolean) =>
    updatePersonaImport({ acceptedTerms: checked });

  const openURL = useCallback((url: string) => {
    if (Platform.OS === 'web') {
      Linking.openURL(url);
    } else {
      WebBrowser.openBrowserAsync(url);
    }
  }, []);

  const toggleCheck = () => setChecked(!checked);

  return (
    <View className="flex-row items-start">
      <Pressable className="p-3" onPress={toggleCheck}>
        <Checkbox
          aria-labelledby="terms"
          checked={checked}
          onCheckedChange={setChecked}
        />
      </Pressable>
      <View className="flex-1 justify-center pt-2">
        <View className="flex-row flex-wrap items-center">
          <Pressable onPress={toggleCheck}>
            <Text>Accept the </Text>
          </Pressable>
          <Pressable
            className="pb-0 pt-0"
            onPress={() => {
              openURL('https://lexikon.ai/terms');
            }}
          >
            <Text className="text-primary underline">terms and conditions</Text>
          </Pressable>
          <Pressable onPress={toggleCheck}>
            <Text> and </Text>
          </Pressable>
          <Pressable
            className="pb-0 pt-0"
            onPress={() => {
              openURL('https://lexikon.ai/privacy');
            }}
          >
            <Text className="text-primary underline">privacy policy</Text>
          </Pressable>
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  image: {
    width: 250,
    height: 250
  }
});
